import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  bookingView,
  fetchBookingHistory,
} from "../../../../../Redux/slice/features/bookingSlice";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";
import { blockingColumns } from "./BlockingTableData";

import DataTable from "../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import { fetchBlockingHistory } from "../../../../../Redux/slice/features/blockingSlice";
import { fetchHotel } from "../../../../../Redux/slice/features/dropdownHotelSlice";
import UpdateBlockModal from "./UpdateBlockModal";

const BlockingTable = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth.user);
  const { data, status, error } = useSelector((state) => state.blockings);
  const hotels = useSelector((state) => state.hotels.hoteldata);
  const [selectedHotel, setSelectedHotel] = useState("");
  const [modalData, setModalData] = useState({ show: false, data: null });

  const currentPage = useSelector((state) => state.pagination.currentPage);
  const itemsPerPage = useSelector((state) => state.pagination.itemsPerPage);

  const userID = user?.customerId;
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    bookingRefNo: "",
    customerName: "",
  });

  const [debouncedCustomer, setDebouncedCustomer] = useState(filters.customerName);

  const handleSelectChange = async (e) => {
    setSelectedHotel(e.target.value);
  };

  useEffect(() => {
    dispatch(fetchHotel());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomer(filters.customerName);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.customerName]);

  useEffect(() => {
    const params = {
      hotelId: selectedHotel || 0,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerName: debouncedCustomer,
      pageNo: currentPage,
      pageSize: itemsPerPage,
    };

    dispatch(fetchBlockingHistory(params));

  }, [
    selectedHotel,
    filters.fromDate,
    filters.toDate,
    debouncedCustomer,
    currentPage,
    itemsPerPage
  ]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [selectedHotel, filters.fromDate, filters.toDate, debouncedCustomer]);

  const columns = useMemo(() => blockingColumns(setModalData), [setModalData]);
  const dataT = useMemo(() => {
    if (status !== "fulfilled") return [];
    return data.map((item) => ({
      BookingRefNo: item.ReleaseID || "",
      hotelName: item.PropertyName || "",
      customerName: item.CustomerName,
      blockFromDate: item.BlockFromDate || "",
      blockToDate: item.BlockToDate || "",
      RoomTypeName: item.RoomTypeName || "",
      BlockRooms: item.BlockRooms,
      Remarks: item.Remarks,
      TotalRooms: item.TotalRooms,
    }));
  }, [data, status]);
  useEffect(() => {
    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
  }, [itemsPerPage]);

  const handleViewBooking = (P_intBookingID) => {
    // console.log("Booking ID", P_intBookingID);
    const receiptUrl = `/booking-view/${P_intBookingID}`;
    window.open(receiptUrl, "_blank", "width=800,height=600");
  };

  const totalRecords = data?.[0]?.TotalCount || 0;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  // console.log('totalPages', totalPages, 'totalRecords', totalRecords);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  const refreshTable = () => {
    const params = {
      hotelId: selectedHotel || 0,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerName: debouncedCustomer,
      pageNo: currentPage,
      pageSize: itemsPerPage,
    };

    dispatch(fetchBlockingHistory(params));
  };

  const clearFilters = () => {
    setSelectedHotel("");

    setFilters({
      fromDate: "",
      toDate: "",
      bookingRefNo: "",
      customerName: "",
    });

    dispatch(setCurrentPage(1));
  };

  return (
    <>
      {(status === "pending" || status === "loading") && <h4>Loading...</h4>}
      {(status === "rejected" || status === "failed") && <p>Error: {error}</p>}
      {status === "fulfilled" && (
        <>
          <div className="col-sm-12 mb-4">
            <div className="card">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-2">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      defaultValue={filters.fromDate}
                      onBlur={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          fromDate: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      defaultValue={filters.toDate}
                      min={filters.fromDate}
                      onBlur={(e) => {
                        if (
                          filters.fromDate &&
                          e.target.value < filters.fromDate
                        ) {
                          alert("To Date cannot be before From Date");
                          return;
                        }

                        setFilters((prev) => ({
                          ...prev,
                          toDate: e.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Hotel Name</label>
                    <select
                      className="form-select"
                      value={selectedHotel}
                      onChange={handleSelectChange}
                    >
                      <option value="">
                        -- Select Hotel --
                      </option>
                      {hotels.map((hotel) => (
                        <option key={hotel.PropertyID} value={hotel.PropertyID}>
                          {hotel.UnitName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Customer Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter customer name"
                      value={filters.customerName}
                      onChange={(e) =>
                        setFilters({ ...filters, customerName: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-secondary w-100"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DataTableHeader columns={columns} data={dataT} />
          <DataTable columns={columns} data={dataT} enablePagination={false} />
          <DataTableFooter
            dataT={dataT}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            totalRecords={totalRecords}
          />
          <UpdateBlockModal
            show={modalData.show}
            onClose={() => setModalData({ show: false, data: null })}
            data={modalData.data}
            refreshTable={refreshTable}
          />
        </>
      )}
    </>
  );
};

export default BlockingTable;
